import React from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BuildIcon from '@mui/icons-material/Build';
import CategoryIcon from '@mui/icons-material/Category';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeIcon from '@mui/icons-material/Home';
import LabelIcon from '@mui/icons-material/Label';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import FolderIcon from '@mui/icons-material/Folder';
import ListIcon from '@mui/icons-material/List';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

/** Stable icon keys stored on asset views — must match backend whitelist. */
export type AssetViewIconKey =
  | 'filter_list'
  | 'inventory'
  | 'build'
  | 'category'
  | 'local_shipping'
  | 'directions_car'
  | 'home'
  | 'label'
  | 'bookmark'
  | 'star'
  | 'check_circle'
  | 'warning'
  | 'folder'
  | 'list'
  | 'handshake'
  | 'precision_manufacturing';

export const DEFAULT_ASSET_VIEW_ICON: AssetViewIconKey = 'filter_list';

export const ASSET_VIEW_ICON_KEYS: AssetViewIconKey[] = [
  'filter_list',
  'inventory',
  'build',
  'category',
  'local_shipping',
  'directions_car',
  'home',
  'label',
  'bookmark',
  'star',
  'check_circle',
  'warning',
  'folder',
  'list',
  'handshake',
  'precision_manufacturing',
];

export const ASSET_VIEW_ICON_DEFINITIONS: { key: AssetViewIconKey; label: string }[] = [
  { key: 'filter_list', label: 'Filter' },
  { key: 'inventory', label: 'Stock' },
  { key: 'build', label: 'Tools' },
  { key: 'category', label: 'Category' },
  { key: 'local_shipping', label: 'Delivery' },
  { key: 'directions_car', label: 'Vehicles' },
  { key: 'home', label: 'Home' },
  { key: 'label', label: 'Tag' },
  { key: 'bookmark', label: 'Bookmark' },
  { key: 'star', label: 'Star' },
  { key: 'check_circle', label: 'Available' },
  { key: 'warning', label: 'Alert' },
  { key: 'folder', label: 'Files' },
  { key: 'list', label: 'List' },
  { key: 'handshake', label: 'Loan' },
  { key: 'precision_manufacturing', label: 'Machinery' },
];

const ICON_COMPONENTS: Record<AssetViewIconKey, React.ElementType> = {
  filter_list: FilterListIcon,
  inventory: Inventory2Icon,
  build: BuildIcon,
  category: CategoryIcon,
  local_shipping: LocalShippingIcon,
  directions_car: DirectionsCarIcon,
  home: HomeIcon,
  label: LabelIcon,
  bookmark: BookmarkIcon,
  star: StarIcon,
  check_circle: CheckCircleIcon,
  warning: WarningIcon,
  folder: FolderIcon,
  list: ListIcon,
  handshake: HandshakeIcon,
  precision_manufacturing: PrecisionManufacturingIcon,
};

export function isAssetViewIconKey(value: unknown): value is AssetViewIconKey {
  return typeof value === 'string' && ASSET_VIEW_ICON_KEYS.includes(value as AssetViewIconKey);
}

export function resolveAssetViewIconKey(value: unknown): AssetViewIconKey {
  return isAssetViewIconKey(value) ? value : DEFAULT_ASSET_VIEW_ICON;
}

/** Render the MUI icon for a stored asset view icon key. */
export function renderAssetViewIcon(icon: unknown, fontSize: 'small' | 'medium' | 'inherit' = 'medium'): React.ReactNode {
  const key = resolveAssetViewIconKey(icon);
  const Icon = ICON_COMPONENTS[key];
  return React.createElement(Icon, { fontSize });
}
